import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, BookOpen, MessageSquare, User, LogOut, ChevronLeft, ChevronRight, Shield, Settings
} from 'lucide-react';

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const links = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/curriculum', icon: BookOpen, label: 'Curriculum' },
    { to: '/coach', icon: MessageSquare, label: 'AI Coach' },
    { to: '/profile', icon: User, label: 'Profile' },
    ...(user?.role === 'admin' ? [{ to: '/admin', icon: Settings, label: 'Admin' }] : []),
  ];

  return (
    <aside
      data-testid="sidebar"
      className={`fixed left-0 top-0 h-screen bg-[#0A0A0A] border-r border-[#262626] flex flex-col z-50 transition-all duration-300 ${collapsed ? 'w-16' : 'w-60'}`}
    >
      <div className="flex items-center gap-2 px-4 h-16 border-b border-[#262626]">
        <Shield className="w-6 h-6 text-[#FF3B30] shrink-0" />
        {!collapsed && <span className="font-heading text-white font-bold text-lg tracking-tight">RiskCoach</span>}
      </div>

      <nav className="flex-1 py-4 flex flex-col gap-1 px-2">
        {links.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200 group ${
                isActive
                  ? 'bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30'
                  : 'text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A]'
              }`
            }
          >
            <Icon className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="text-sm font-medium">{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#262626] p-3">
        {!collapsed && user && (
          <div className="mb-3 px-2">
            <p className="text-white text-sm font-medium truncate">{user.name}</p>
            <p className="text-[#737373] text-xs truncate">{user.email}</p>
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 w-full text-[#A3A3A3] hover:text-[#FF3B30] hover:bg-[#1A1A1A] rounded-sm transition-all"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>

      <button
        data-testid="toggle-sidebar"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 bg-[#141414] border border-[#262626] rounded-full p-1 text-[#A3A3A3] hover:text-white hover:border-[#FF3B30] transition-all"
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
      </button>
    </aside>
  );
}
